import { useQuery } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { ChevronRight, FileText, Calendar as CalendarIcon, AlertTriangle } from "lucide-react"
import { PageHeader } from "@/components/portal/PageHeader"
import { Loading, ErrorBox, fmtRelative } from "@/components/portal/LoadingError"
import { VORGANG_OHNE_NAME } from "@/lib/vorgangName"

type SwitchEvent = {
  created_at: string
  project_match_id: number | null
  project_nr: string
  project_name: string
  trigger_id: number | null
  trigger_label: string
  document_nr: string
  document_status_name: string
  old_status: string
  new_status: string
}

type SkippedSwitch = {
  created_at: string
  project_match_id: number | null
  project_nr: string
  project_name: string
  document_id: number | null
  document_nr: string
  document_type: string
  document_status_name: string
  target_step_name: string
  reason: string
}

type RuleHit = {
  project_type_id: number
  project_type_name: string
  trigger_filter_id: number
  trigger_filter_name: string
  target_step_id: number
  target_step_name: string
  hit_count: number
  last_hit_at: string | null
  events: SwitchEvent[]
}

type Summary = {
  tenant_slug: string
  since: string
  project_type_names: Record<string, string>
  doc_switches: RuleHit[]
  appt_switches: RuleHit[]
  skipped: SkippedSwitch[]
}

function fmtFullDate(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    })
  } catch {
    return iso
  }
}

const SINCE_OPTIONS: { value: "24h" | "7d" | "30d" | "all"; label: string }[] = [
  { value: "24h", label: "24h" },
  { value: "7d", label: "7 Tage" },
  { value: "30d", label: "30 Tage" },
  { value: "all", label: "Alle" },
]

async function fetchTenantList(): Promise<{ slug: string; name: string }[]> {
  const r = await fetch("/api/v1/tenants?include_test=true", { credentials: "include" })
  if (!r.ok) return []
  const d = await r.json()
  return (d.items || []).map((t: Record<string, unknown>) => ({
    slug: String(t.slug || ""),
    name: String(t.name || t.slug || ""),
  }))
}

async function fetchSummary(tenant: string, since: string): Promise<Summary> {
  const r = await fetch(
    `/api/v1/auto-switches/summary?tenant=${encodeURIComponent(tenant)}&since=${encodeURIComponent(since)}`,
    { credentials: "include" },
  )
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

export default function AutoSwitchesPage() {
  const [tenant, setTenant] = useState<string>("")
  const [since, setSince] = useState<"24h" | "7d" | "30d" | "all">("7d")

  const tenants = useQuery({ queryKey: ["tenants-min"], queryFn: fetchTenantList, staleTime: 60_000 })

  // Default-Tenant beim ersten Laden setzen
  useMemo(() => {
    if (!tenant && tenants.data && tenants.data.length > 0) {
      // Bevorzugt sanitec_haustechnik wenn vorhanden, sonst erster
      const preferred = tenants.data.find((t) => t.slug === "sanitec_haustechnik")
      setTenant(preferred?.slug || tenants.data[0].slug)
    }
  }, [tenants.data, tenant])

  const summary = useQuery({
    queryKey: ["auto-switches", tenant, since],
    queryFn: () => fetchSummary(tenant, since),
    enabled: !!tenant,
    staleTime: 15_000,
  })

  const totalDoc = (summary.data?.doc_switches || []).reduce((s, h) => s + h.hit_count, 0)
  const totalAppt = (summary.data?.appt_switches || []).reduce((s, h) => s + h.hit_count, 0)

  return (
    <div className="px-10 py-8 max-w-[1200px]">
      <PageHeader
        title="Auto-Status-Switches"
        count={totalDoc + totalAppt}
        onRefresh={() => summary.refetch()}
        refreshing={summary.isFetching}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            value={tenant}
            onChange={(e) => setTenant(e.target.value)}
            className="pp-input"
            style={{ fontSize: 13, padding: "5px 10px" }}
          >
            {(tenants.data || []).map((t) => (
              <option key={t.slug} value={t.slug}>{t.name}</option>
            ))}
          </select>
          <select
            value={since}
            onChange={(e) => setSince(e.target.value as "24h" | "7d" | "30d" | "all")}
            className="pp-input"
            style={{ fontSize: 13, padding: "5px 10px" }}
          >
            {SINCE_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </PageHeader>

      <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4, marginBottom: 24, maxWidth: 720, lineHeight: 1.5 }}>
        Übersicht aller Projekt-Status-Switches die das Portal in den letzten {since === "all" ? "verfügbaren" : SINCE_OPTIONS.find((s) => s.value === since)?.label}
        {" "}automatisch ausgeführt hat — gruppiert nach Regel. Quelle: <strong>Doc-Status-Switch</strong> (Dokument in HERO erstellt
        <em> und versendet</em>) und <strong>Termin-Status-Switch</strong> (Termin-Ende erreicht).{" "}
        Dokumente im Status &laquo;Entwurf&raquo; lösen keinen Switch aus &mdash; siehe Sektion <em>Übersprungen</em> weiter unten.
      </p>

      {summary.error ? (
        <ErrorBox error={summary.error} />
      ) : summary.isLoading || !summary.data ? (
        <Loading label="Lädt..." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          <RuleSection
            title="Status nach Dokument-Erstellung"
            icon={<FileText className="w-4 h-4" />}
            rules={summary.data.doc_switches}
            triggerLabel="Dokumenttyp"
          />
          <RuleSection
            title="Status nach Termin-Durchführung"
            icon={<CalendarIcon className="w-4 h-4" />}
            rules={summary.data.appt_switches}
            triggerLabel="Kategorie"
          />
          <SkippedSection skipped={summary.data.skipped || []} />
        </div>
      )}
    </div>
  )
}

function SkippedSection({ skipped }: { skipped: SkippedSwitch[] }) {
  const [open, setOpen] = useState(false)
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <AlertTriangle className="w-4 h-4" style={{ color: "#B45309" }} />
        <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Übersprungene Switches</h2>
        <span style={{
          fontSize: 12, color: "var(--text-muted)",
          background: "#FEF3C7", padding: "2px 8px", borderRadius: 4,
        }}>{skipped.length}</span>
        <span style={{ fontSize: 12, color: "var(--text-muted)", marginLeft: 4 }}>
          — Dokument im Status &laquo;Entwurf&raquo; oder &laquo;Erstellt&raquo;, noch nicht versendet
        </span>
      </div>
      {skipped.length === 0 ? (
        <div className="pp-card" style={{ padding: 16, textAlign: "center", color: "var(--text-muted)", fontSize: 12.5 }}>
          Aktuell wurden keine Switches wegen fehlendem Versand übersprungen.
        </div>
      ) : (
        <div className="pp-card" style={{ padding: 0, overflow: "hidden" }}>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            style={{
              width: "100%", textAlign: "left", padding: "12px 16px",
              background: "transparent", border: "none", cursor: "pointer",
              fontSize: 13, color: "var(--text-muted)",
              borderBottom: open ? "0.5px solid rgba(44,44,42,0.1)" : "none",
            }}
          >
            {open ? "▾ Zuklappen" : "▸ Liste aufklappen"} — der nächste Polling-Lauf nach Versand löst den Switch automatisch aus.
          </button>
          {open && (
            <div style={{ padding: "4px 16px 12px" }}>
              {skipped.map((s, i) => {
                // #98: nie „#7284194" — der Titel steht direkt daneben.
                const projLabel = s.project_nr || VORGANG_OHNE_NAME
                return (
                  <div key={i} style={{
                    display: "flex", justifyContent: "space-between", gap: 12,
                    fontSize: 12, padding: "8px 0",
                    borderBottom: i < skipped.length - 1 ? "0.5px solid rgba(44,44,42,0.05)" : "none",
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        {s.project_match_id && (
                          <a
                            href={`/projekte/${s.project_match_id}`}
                            style={{ color: "#3C3489", textDecoration: "none", fontWeight: 600 }}
                          >{projLabel}</a>
                        )}
                        {s.project_name && (
                          <span style={{ color: "#2C2C2A" }}>· {s.project_name}</span>
                        )}
                      </div>
                      <div style={{ color: "var(--text-muted)", marginTop: 2, fontSize: 12 }}>
                        {s.document_type} {s.document_nr} —{" "}
                        <strong style={{ fontWeight: 600, color: "#B45309" }}>{s.document_status_name || "kein Status"}</strong>
                        {s.target_step_name && (
                          <> · Switch zu &laquo;{s.target_step_name}&raquo; wartet auf Versand</>
                        )}
                      </div>
                    </div>
                    <span
                      style={{ color: "var(--text-muted)", flexShrink: 0, whiteSpace: "nowrap" }}
                      title={fmtFullDate(s.created_at)}
                    >{fmtFullDate(s.created_at)}</span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function RuleSection({
  title, icon, rules, triggerLabel,
}: {
  title: string
  icon: React.ReactNode
  rules: RuleHit[]
  triggerLabel: string
}) {
  const total = rules.reduce((s, r) => s + r.hit_count, 0)
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        {icon}
        <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>{title}</h2>
        <span style={{
          fontSize: 12, color: "var(--text-muted)",
          background: "#F3F2EE", padding: "2px 8px", borderRadius: 4,
        }}>{total} Switches</span>
      </div>
      {rules.length === 0 ? (
        <div className="pp-card" style={{ padding: 20, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
          Keine Switches im gewählten Zeitraum.
        </div>
      ) : (
        <div style={{
          display: "grid",
          // 420 px Mindestbreite erzwang auf schmalen Fenstern eine
          // Karte, die breiter war als der Platz — 300 px reichen fuer
          // den Inhalt, und `auto-fill` macht daraus wieder mehrere
          // Spalten, sobald Platz da ist.
          gridTemplateColumns: "repeat(auto-fill, minmax(min(300px, 100%), 1fr))",
          gap: 12,
        }}>
          {rules.map((r, i) => (
            <RuleCard key={i} rule={r} triggerLabel={triggerLabel} />
          ))}
        </div>
      )}
    </div>
  )
}

function RuleCard({ rule, triggerLabel }: { rule: RuleHit; triggerLabel: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="pp-card" style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.3 }}>
            {rule.project_type_name || `Typ ${rule.project_type_id}`}
          </div>
          <div style={{ fontSize: 14, fontWeight: 500, marginTop: 4 }}>
            <span style={{ color: "var(--text-muted)" }}>{triggerLabel}:</span> {rule.trigger_filter_name || `#${rule.trigger_filter_id}`}
          </div>
          <div style={{ fontSize: 13, color: "#3C3489", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
            <ChevronRight className="w-3 h-3" />
            <span style={{ fontWeight: 500 }}>{rule.target_step_name || `Step ${rule.target_step_id}`}</span>
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 24, fontWeight: 600, color: "#3C3489", lineHeight: 1 }}>{rule.hit_count}</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Treffer</div>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "0.5px solid rgba(44,44,42,0.1)", paddingTop: 8, marginTop: 8 }}>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          zuletzt: {rule.last_hit_at ? fmtRelative(rule.last_hit_at) : "—"}
        </span>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          style={{
            fontSize: 12, color: "#3C3489", background: "transparent", border: "none",
            cursor: "pointer", padding: 0, fontWeight: 500,
          }}
        >{open ? "Einklappen" : "Liste aufklappen"}</button>
      </div>
      {open && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "0.5px dashed rgba(44,44,42,0.15)" }}>
          {rule.events.map((e, i) => {
            // Projekt-Label: project_nr (HEZ-733), sonst neutral — nie „#ID"
            // (#98). Der Titel steht direkt daneben.
            const projLabel = e.project_nr || VORGANG_OHNE_NAME
            // Doc-Label: Doc-Nr + Status, sonst trigger_label
            const docLabel = e.document_nr
              ? `${e.document_nr}${e.document_status_name ? ` · ${e.document_status_name}` : ""}`
              : e.trigger_label
            return (
              <div key={i} style={{
                display: "flex", justifyContent: "space-between", gap: 12,
                fontSize: 12, padding: "8px 0",
                borderBottom: i < rule.events.length - 1 ? "0.5px solid rgba(44,44,42,0.05)" : "none",
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    {e.project_match_id && (
                      <a
                        href={`/projekte/${e.project_match_id}`}
                        style={{ color: "#3C3489", textDecoration: "none", fontWeight: 600 }}
                      >{projLabel}</a>
                    )}
                    {e.project_name && (
                      <span style={{ color: "#2C2C2A" }} title={e.project_name}>· {e.project_name}</span>
                    )}
                  </div>
                  {docLabel && (
                    <div style={{ color: "var(--text-muted)", marginTop: 2, fontSize: 12 }}>
                      Trigger: {docLabel}
                    </div>
                  )}
                  {(e.old_status || e.new_status) && (
                    <div style={{ color: "var(--text-muted)", marginTop: 2, fontSize: 12 }}>
                      {e.old_status ? (
                        <>Status: <strong style={{ fontWeight: 500 }}>{e.old_status}</strong>{" → "}<strong style={{ color: "#3C3489" }}>{e.new_status}</strong></>
                      ) : (
                        <>Neuer Status: <strong style={{ color: "#3C3489" }}>{e.new_status}</strong></>
                      )}
                    </div>
                  )}
                </div>
                <span
                  style={{ color: "var(--text-muted)", flexShrink: 0, whiteSpace: "nowrap" }}
                  title={fmtFullDate(e.created_at)}
                >{fmtFullDate(e.created_at)}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
